
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

interface Roommate {
  id: string;
  name: string;
}

export interface Hotel {
  id: string;
  name: string;
  address: string;
  checkIn: string;
  checkOut: string;
  confirmation: string;
  notes: string;
  price: number;
  phone: string;
  addedBy?: string;
  roommates?: Roommate[];
  tripId?: string;
}

export const useHotelsList = (tripId: string) => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [editingHotelId, setEditingHotelId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const loadHotels = () => {
      const savedHotels = JSON.parse(localStorage.getItem('manualHotels') || '[]');
      
      // Filter hotels by the current tripId
      const tripHotels = savedHotels.filter((hotel: Hotel) => hotel.tripId === tripId);
      
      // Process the hotels to ensure all required fields exist
      const processedHotels = tripHotels.map((hotel: Hotel) => ({
        ...hotel,
        addedBy: hotel.addedBy || 'Unknown',
        // Explicitly handle the roommates array - ensure it's properly initialized
        roommates: Array.isArray(hotel.roommates) ? hotel.roommates : []
      }));
      
      console.log('Processed hotels for trip', tripId, ':', processedHotels);
      setHotels(processedHotels);
    };

    loadHotels();
    window.addEventListener('storage', loadHotels);
    
    return () => {
      window.removeEventListener('storage', loadHotels);
    };
  }, [tripId]);

  const removeHotel = (id: string) => {
    const allHotels = JSON.parse(localStorage.getItem('manualHotels') || '[]');
    const updatedHotels = allHotels.filter((hotel: Hotel) => hotel.id !== id);
    localStorage.setItem('manualHotels', JSON.stringify(updatedHotels));
    
    // Update state with only the hotels for this trip
    setHotels(hotels.filter(hotel => hotel.id !== id));
    toast.success('Hotel removed from your trip');
    
    // Trigger storage event for the HotelForm component
    window.dispatchEvent(new Event('storage'));
  };

  const editHotel = (id: string) => {
    setEditingHotelId(id);
    
    // Scroll to the edit form
    setTimeout(() => {
      const formElement = document.getElementById('hotel-edit-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleEditComplete = () => {
    setEditingHotelId(undefined);
  };

  return {
    hotels,
    editingHotelId,
    removeHotel,
    editHotel,
    handleEditComplete
  };
};
